import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  BlueberryFeatures,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { isBlueberryFeature } from '../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { useBlueberryFeatureIcon } from '../commonUI/BlueberryIcons';
import LoadingSpinner from '../commonUI/LoadingSpinner';
import { flexCenter } from '../commonUI/cssClasses';
import theme from '../commonUI/theme';
import { MeasurementTypes, getUnitSymbol } from '../commonUtils';
import { useBlueberryConfig } from '../dataMesh/blueberry/hooks/useBlueberryConfig';
import { roundFractional } from '../ranches/ranchUtils';
import { useBlueberryFlowerFeatures } from '../scanDataApi/hooks/useBlueberryFlowerFeatures';
import { BlueberryData } from '../scanDataApi/hooks/useBlueberryQuery';
import useScanData from '../scanDataApi/hooks/useScanData';
import {
  ScanData,
  ScanDataMeasureEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataApi/scanDataDefinitions';
import { orderBlueberriesByRipenessOrder } from '../scanDataApi/scanDataUtils';
import { selectCurrFeature, selectCurrMeasure } from '../select/selectSlice';
import { useUnits } from '../settings/hooks/useUnits';
import { PointProperties } from './Mapbox';
import StyledPopup from './StyledPopup';
import { convertStringToLatLng } from './mapUtils';
interface Props {
  pointProperties: PointProperties | null;
}

type MeasureInfoType = (
  pointData: ScanData,
  pointProperties: PointProperties
) => {
  render?: React.FC;
  label?: string;
  value?: number | string;
};

const PointInfoPopup: React.FC<Props> = ({ pointProperties }) => {
  const currFeature = useAppSelector(selectCurrFeature);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const units = useUnits();
  const { currHoverPointScanData, blueberryHoverPointData } = useScanData();
  const { t } = useTranslation();
  const blueberryConfig = useBlueberryConfig();
  const includeFlowers = blueberryConfig?.includeFlowers;

  const blueberryFlowerFeatures = useBlueberryFlowerFeatures();

  const MeasureInfoDict: { [key: string]: MeasureInfoType } = useMemo(() => {
    return {
      [ScanDataMeasureEnum.color_hsv]: (pointData, pointProperties) => ({
        render: () => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Typography>{t('color')}</Typography>
            <Box>
              <Box
                sx={{
                  backgroundColor: pointProperties.color,
                  width: '24px',
                  height: '24px',
                  maxWidth: '24px',
                  maxHeight: '24px',
                  margin: '8px',
                  borderRadius: '50%',
                }}
              />
            </Box>
          </Box>
        ),
      }),
      [ScanDataMeasureEnum.detection_length]: (pointData) => ({
        label:
          currFeature === FeatureEnum.berries
            ? `${t('Diameter')}`
            : `${t(pointData.class_name)} ${t('Length', {
                ns: 'common',
              })}`,
        value:
          pointData.detection_length != null
            ? `${roundFractional(
                pointData.detection_length,
                1
              )} ${getUnitSymbol(units, MeasurementTypes.millimeterOrInch, t)}`
            : 'N/D',
      }),
      [ScanDataMeasureEnum.count_per_meter]: (pointData) => ({
        label: `${t(currFeature)} / ${getUnitSymbol(
          units,
          MeasurementTypes.meterOrFeet,
          t
        )}`,
        value:
          pointData.count_per_meter != null
            ? roundFractional(pointData.count_per_meter, 2)
            : 'N/D',
      }),
      [ScanDataMeasureEnum.detection_area]: (pointData) => ({
        label: t('detection_area'),
        value:
          pointData.detection_area != null
            ? `${roundFractional(pointData.detection_area, 2)} ${getUnitSymbol(
                units,
                MeasurementTypes.metersOrFeetSquared,
                t
              )}`
            : 'N/D',
      }),
    };
  }, [currFeature, units, t]);

  const popupInner = useCallback(
    (pointProperties: PointProperties) => {
      if (
        currHoverPointScanData.isLoading ||
        blueberryHoverPointData.isLoading
      ) {
        return (
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <LoadingSpinner />
          </Box>
        );
      }

      if (currHoverPointScanData.error || blueberryHoverPointData.error) {
        return <Typography>{t('loadingError')}</Typography>;
      }

      const isBlueberry = isBlueberryFeature(currFeature);

      if (
        (!isBlueberry && !currHoverPointScanData.data?.length) ||
        (isBlueberry && !blueberryHoverPointData.data?.length)
      ) {
        return <Typography>{t('noData')}</Typography>;
      }

      if (isBlueberry && blueberryHoverPointData.data?.length) {
        const blueberryPoint = blueberryHoverPointData.data[0];
        return (
          <Table>
            <TableHead>
              <TableCell />
              <TableCell>
                <Typography align={'center'}>#</Typography>
              </TableCell>
              <TableCell>
                <Typography align={'center'}>
                  /{getUnitSymbol(units, MeasurementTypes.meterOrFeet, t)}
                </Typography>
              </TableCell>
            </TableHead>
            <TableBody>
              {includeFlowers && (
                <>
                  {orderBlueberriesByRipenessOrder({
                    arrayToOrder: blueberryFlowerFeatures,
                  }).map((feature) => (
                    <BlueberryPointItem
                      key={feature}
                      blueberryPoint={blueberryPoint}
                      feature={feature}
                    />
                  ))}
                  {blueberryFlowerFeatures.length > 1 && (
                    <BlueberrySummaryItem
                      blueberryPoint={blueberryPoint}
                      summaryFeature={FeatureEnum.flower}
                    />
                  )}
                  <TableDivider columns={3} />
                </>
              )}
              {orderBlueberriesByRipenessOrder({
                arrayToOrder: BlueberryFeatures,
              }).map((feature) => (
                <BlueberryPointItem
                  key={feature}
                  blueberryPoint={blueberryPoint}
                  feature={feature}
                />
              ))}
              <BlueberrySummaryItem
                blueberryPoint={blueberryPoint}
                summaryFeature={FeatureEnum.blueberry}
              />
              <TableDivider columns={3} />
              <BlueberrySummaryItem blueberryPoint={blueberryPoint} />
            </TableBody>
          </Table>
        );
      }

      const currScanDataMeasure =
        convertMeasureCubeRanchToScanDataBlock(currMeasure);

      if (currScanDataMeasure && currHoverPointScanData.data?.length) {
        const currInfo = MeasureInfoDict[currScanDataMeasure](
          currHoverPointScanData.data[0],
          pointProperties
        );
        const rawCount = currHoverPointScanData.data[0].raw_count;

        if (currInfo.render != null) {
          return <currInfo.render />;
        }

        if (currInfo.label != null && currInfo.value != null) {
          return (
            <Table>
              <TableBody>
                <SummaryItem
                  label={t(currInfo.label)}
                  measure={currInfo.value}
                />
                {rawCount != null && (
                  <SummaryItem label={t('Raw Count')} measure={rawCount} />
                )}
              </TableBody>
            </Table>
          );
        }
      }

      return <Typography>{t('noInfo')}</Typography>;
    },
    [
      currMeasure,
      currHoverPointScanData,
      blueberryHoverPointData,
      blueberryFlowerFeatures,
      includeFlowers,
      pointProperties,
      t,
    ]
  );

  if (!pointProperties) return null;
  const latLong = convertStringToLatLng(pointProperties?.point_location);

  if (!latLong) return null;

  return (
    <StyledPopup
      longitude={latLong.longitude}
      latitude={latLong.latitude}
      offset={15}
      closeButton={false}
      className='point-info-popup'
      closeOnClick={false}
    >
      <Typography
        sx={{
          width: '100%',
          ...flexCenter,
        }}
      >
        {`Row: ${pointProperties.rowside_num} (${pointProperties.view_direction})`}
      </Typography>
      {popupInner(pointProperties)}
    </StyledPopup>
  );
};

export default PointInfoPopup;

interface BlueberryPointItemProps {
  blueberryPoint: BlueberryData;
  feature: FeatureEnum;
}

interface BlueberrySummaryPointProps {
  blueberryPoint: BlueberryData;
  summaryFeature?: FeatureEnum.blueberry | FeatureEnum.flower | null;
}

interface SummaryItemProps {
  label: string;
  measure: number | string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ label, measure }) => {
  return (
    <TableRow>
      <TableCell>
        <Typography noWrap>{label}</Typography>
      </TableCell>
      <TableCell>
        <Typography noWrap>{measure}</Typography>
      </TableCell>
    </TableRow>
  );
};

const TableDivider = ({ columns = 2 }) => {
  return (
    <TableRow>
      <TableCell colSpan={columns}>
        <Divider
          sx={{ margin: '0.25rem 0' }}
          color={theme.colors.darkTheme.grey.muted}
        />
      </TableCell>
    </TableRow>
  );
};

type BlueberryClassType =
  | FeatureEnum.blueberry_Blue
  | FeatureEnum.blueberry_Green
  | FeatureEnum.blueberry_Purple
  | FeatureEnum.blueberry_Yellow;

const BlueberrySummaryItem: React.FC<BlueberrySummaryPointProps> = ({
  blueberryPoint,
  summaryFeature = null,
}) => {
  const { t } = useTranslation();

  const flowerFeatures = useBlueberryFlowerFeatures();
  const allBlueberryFeatuers = [...BlueberryFeatures, ...flowerFeatures];

  const featuresToCount =
    summaryFeature === null
      ? allBlueberryFeatuers
      : summaryFeature === FeatureEnum.flower
      ? flowerFeatures
      : BlueberryFeatures;

  const displayLabel =
    summaryFeature === null
      ? 'Total'
      : summaryFeature === FeatureEnum.flower
      ? 'Total Flowers'
      : 'Total Fruit';

  const totalCount = featuresToCount.reduce((acc, el) => {
    const rawCount = blueberryPoint?.[el as BlueberryClassType]?.raw_count;

    if (rawCount != null) {
      acc += rawCount;
    }
    return acc;
  }, 0);
  const totalCountPerMeter = featuresToCount.reduce((acc, el) => {
    const countPerMeter =
      blueberryPoint?.[el as BlueberryClassType]?.count_per_meter;

    if (countPerMeter != null) {
      acc += countPerMeter;
    }
    return acc;
  }, 0);

  return (
    <TableRow>
      <TableCell>
        <Typography>{t(displayLabel)}</Typography>
      </TableCell>
      <TableCell>
        <Typography align={'center'}>{totalCount}</Typography>
      </TableCell>
      <TableCell>
        <Typography align={'center'}>
          {roundFractional(totalCountPerMeter, 1)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const BlueberryPointItem: React.FC<BlueberryPointItemProps> = ({
  blueberryPoint,
  feature,
}) => {
  const { t } = useTranslation();
  const rawCount = blueberryPoint?.[feature as BlueberryClassType]?.raw_count;
  const countPerMeter =
    blueberryPoint?.[feature as BlueberryClassType]?.count_per_meter;

  const rawCountDisplayValue = rawCount != null ? rawCount : 'N/D';
  const countPerMeterDisplayValue =
    countPerMeter != null ? roundFractional(countPerMeter, 0) : 'N/D';

  const getBlueberryIcon = useBlueberryFeatureIcon();
  const theme = useTheme();

  const blueberryTextColorMap: { [key: string]: string } = {
    [FeatureEnum.blueberry_Green]: theme.colors.blueberryColors.green,
    [FeatureEnum.blueberry_Yellow]: theme.colors.blueberryColors.pink,
    [FeatureEnum.blueberry_Purple]: theme.colors.blueberryColors.purple,
    [FeatureEnum.blueberry_Blue]: theme.colors.blueberryColors.blue,
    [FeatureEnum.flower]: theme.colors.blueberryColors.flower,
    [FeatureEnum.flower_closed]: theme.colors.blueberryColors.closed,
    [FeatureEnum.flower_open]: theme.colors.blueberryColors.open,
    [FeatureEnum.flower_petalfall]: theme.colors.blueberryColors.petalfall,
  };

  return (
    <TableRow>
      <TableCell
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '12px',
            height: '12px',
            aspectRatio: '1/1',
            mr: '0.5rem',
            mb: '1.5px',
          }}
        >
          {getBlueberryIcon(feature, 0.8)}
        </Box>
        <Typography
          sx={{
            color: `${blueberryTextColorMap[feature]} !important`,
            mr: '0.5rem',
          }}
        >
          {/* Make flowers plural */}
          {feature === FeatureEnum.flower ? t('Flowers') : t(feature)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography align={'center'}>{rawCountDisplayValue}</Typography>
      </TableCell>
      <TableCell>
        <Typography align={'center'}>{countPerMeterDisplayValue}</Typography>
      </TableCell>
    </TableRow>
  );
};
